import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHistory, faSpinner, faFilter } from '@fortawesome/free-solid-svg-icons';
import { adminAuditAPI } from '../../services/adminAPI';
import { toast } from 'react-toastify';
import './admin.css';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterAdmin, setFilterAdmin] = useState('');
  const [actions, setActions] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total: 0,
    pages: 1
  });

  useEffect(() => {
    fetchActions();
    fetchAdmins();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [filterAction, filterType, filterAdmin, pagination.current_page]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current_page,
        per_page: 50,
        action: filterAction || undefined,
        target_type: filterType || undefined,
        admin_id: filterAdmin || undefined
      };

      const response = await adminAuditAPI.getLogs(params);
      setLogs(response.data.logs);
      setPagination({
        current_page: response.data.current_page,
        total: response.data.total,
        pages: response.data.pages
      });
    } catch (error) {
      toast.error('Failed to fetch audit logs');
      console.error('Fetch logs error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActions = async () => {
    try {
      const response = await adminAuditAPI.getActions();
      setActions(response.data.actions);
    } catch (error) {
      console.error('Fetch actions error:', error);
    }
  };

  const fetchAdmins = async () => {
    try {
      const response = await adminAuditAPI.getAdmins();
      setAdmins(response.data.admins);
    } catch (error) {
      console.error('Fetch admins error:', error);
    }
  };

  const getActionBadgeClass = (action) => {
    if (action.includes('created')) return 'success';
    if (action.includes('updated')) return 'primary';
    if (action.includes('deleted') || action.includes('deactivated')) return 'danger';
    if (action.includes('activated')) return 'success';
    return 'warning';
  };

  const formatDetails = (details) => {
    try {
      const parsed = JSON.parse(details);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return details;
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>
          <FontAwesomeIcon icon={faHistory} />
          Audit Logs
        </h1>
        <p>View all administrative actions and system changes</p>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">
            <FontAwesomeIcon icon={faFilter} />
            Audit Trail ({pagination.total} records)
          </h3>
        </div>

        <div className="admin-filter-bar">
          <select
            className="admin-select"
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
          >
            <option value="">All Actions</option>
            {actions.map((action) => (
              <option key={action} value={action}>
                {action}
              </option>
            ))}
          </select>

          <select
            className="admin-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">All Types</option>
            <option value="user">User</option>
            <option value="company">Company</option>
            <option value="donation_cause">Donation Cause</option>
            <option value="super_admin">Super Admin</option>
          </select>

          <select
            className="admin-select"
            value={filterAdmin}
            onChange={(e) => setFilterAdmin(e.target.value)}
          >
            <option value="">All Admins</option>
            {admins.map((admin) => (
              <option key={admin.id} value={admin.id}>
                {admin.username}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="admin-loading">
            <FontAwesomeIcon icon={faSpinner} spin />
            <p>Loading audit logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="admin-empty-state">
            <FontAwesomeIcon icon={faHistory} />
            <h3>No Audit Logs Found</h3>
            <p>No actions match your current filters</p>
          </div>
        ) : (
          <>
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Admin</th>
                    <th>Action</th>
                    <th>Target</th>
                    <th>Details</th>
                    <th>IP Address</th>
                    <th>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id}>
                      <td data-label="ID">{log.id}</td>
                      <td data-label="Admin">{log.admin_username || 'Unknown'}</td>
                      <td data-label="Action">
                        <span className={`admin-badge ${getActionBadgeClass(log.action)}`}>
                          {log.action}
                        </span>
                      </td>
                      <td data-label="Target">
                        {log.target_type}
                        {log.target_id && ` #${log.target_id}`}
                      </td>
                      <td data-label="Details">
                        <details>
                          <summary style={{ cursor: 'pointer', color: 'var(--admin-primary)' }}>
                            View Details
                          </summary>
                          <pre style={{
                            marginTop: '0.5rem',
                            padding: '0.5rem',
                            background: 'var(--admin-bg)',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            maxWidth: '400px',
                            overflow: 'auto'
                          }}>
                            {formatDetails(log.details)}
                          </pre>
                        </details>
                      </td>
                      <td data-label="IP Address">{log.ip_address || 'N/A'}</td>
                      <td data-label="Timestamp">{new Date(log.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.pages > 1 && (
              <div className="admin-pagination">
                <button
                  disabled={pagination.current_page === 1}
                  onClick={() => setPagination({...pagination, current_page: pagination.current_page - 1})}
                >
                  Previous
                </button>
                <span style={{ color: 'var(--admin-text)' }}>
                  Page {pagination.current_page} of {pagination.pages}
                </span>
                <button
                  disabled={pagination.current_page === pagination.pages}
                  onClick={() => setPagination({...pagination, current_page: pagination.current_page + 1})}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AuditLogs;
